import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, HostListener, AfterViewInit, NgZone, ViewChild, ComponentFactoryResolver } from '@angular/core';
import { RoutesRecognized } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest, Observable } from 'rxjs';
import { takeUntil, map, take } from 'rxjs/operators';

import { SplashScreenService } from '@core-services/splash-screen.service';
import { SidebarService } from '@core-layout/sidebar/sidebar.service';
import { CommunicationSignalRService } from '@notifications/services/communication-signalr.service';
import { SyncLoginStateService } from '@auth/services/sync-login-state.service';
import { ApplicationUpdateService } from '@notifications/services/application-update.service';
import { CustomerActivityService } from '@customer-activity/customer-activity.service';
import { AppConfigurationService } from '@app-config/services/app-configuration.service';
import { AppConfiguration } from '@app-config/models/app-configuration';
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/overlay';
import { DynamicComponentHostDirective } from '@core-directives/dynamic-component-host/dynamic-component-host';
import { UserStoreService } from '@auth/store/services/user-store.service';
import { RouteService } from './services/route.service';
import { PageRefresherService } from './services/page-refresher.service';
import { SettingsStoreService } from '@settings/store/services/settings-store.service';
import { AppStoreService } from './store/services/app-store.service';
import { AgentsStoreService } from '@agents/store/services/agents-store.service';
import { CollaborationSpaceStoreService } from '@auth/store/services/collaboration-space-store.service';
import { UsersStoreService } from '@users/store/services/users-store.service';
import { ProfileBaseStoreService } from 'app/modules/user-modules/profile-modules/profile-base/store/services/profile-base-store.service';
import { NotificationsStoreService } from '@notifications/store/services/notifications-store.service';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

    private readonly unsubscribe$ = new Subject<void>();
    private static readonly MinimizedSecondaryToolbarScrollLength = 20;

    @ViewChild(DynamicComponentHostDirective) dynamicComponentHost: DynamicComponentHostDirective;

    public configuration: AppConfiguration;
    public hasThemeSidebar = false;
    public minimizedToolbar: boolean;
    public isUserAuthorized: boolean;
    public isDarkMode: boolean;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly configurationService: AppConfigurationService,
        private readonly sidebarService: SidebarService,
        // SplashScreenService must be injected for initialization
        private readonly splashScreenService: SplashScreenService,
        private readonly translateService: TranslateService,
        private readonly titleService: Title,
        private readonly communicationSignalRService: CommunicationSignalRService,
        private readonly syncLoginStateService: SyncLoginStateService,
        private readonly applicationUpdateService: ApplicationUpdateService,
        private readonly customerActivityService: CustomerActivityService,
        private readonly scrollDispatcher: ScrollDispatcher,
        private readonly ngZone: NgZone,
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly userStoreService: UserStoreService,
        private readonly routeService: RouteService,
        private readonly settingsStoreService: SettingsStoreService,
        private readonly appStoreService: AppStoreService,
        private readonly agentsStoreService: AgentsStoreService,
        private readonly usersStoreService: UsersStoreService,
        @Inject('IS_PRODUCTION') private readonly isProduction: boolean,
        @Inject('IS_STAGE') private readonly isStage: boolean,
        private readonly collaborationSpaceStoreService: CollaborationSpaceStoreService,
        private readonly profileBaseStoreService: ProfileBaseStoreService,
        private readonly notificationsStoreService: NotificationsStoreService,
    ) {
        this.splashScreenService;
        this.translateService.addLangs(['en']);
        this.translateService.setDefaultLang('en');
        this.translateService.use('en');

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
            this._translateService.setDefaultLang('en');
            this._translateService.setDefaultLang('tr');
         });
         */
    }

    public ngOnInit(): void {
        this.subscribeToLayoutReset();
        this.createAppConfigurationSubscription();
        this.createCompanyConfigurationSubscription();
        this.routeService.getCurrentRouteDataProperty<boolean>('hasThemeSidebar')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(hasThemeSidebar => {
                this.hasThemeSidebar = !this.isProduction && !this.isStage && hasThemeSidebar != null && hasThemeSidebar;
            });
        this.routeService.getNavigateStartCurrentRoute()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => this.sidebarService.closeAll());
        this.syncLoginStateService.initialize();
        this.customerActivityService.initialize().catch(() => { });
        this.applicationUpdateService.initialize(this.unsubscribe$);
        this.loadSettings();

        this.scrollDispatcher.scrolled()
            .pipe(map((event: CdkScrollable) =>
                event != null ? event.getElementRef().nativeElement.scrollTop : window.scrollY))
            .subscribe(scrolledLength =>
                this.ngZone.run(() =>
                    this.minimizedToolbar = scrolledLength > AppComponent.MinimizedSecondaryToolbarScrollLength));

        this.userStoreService.getIsAuthorized()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(isUserAuthorized => {
                this.isUserAuthorized = isUserAuthorized;

                if (this.isUserAuthorized) {
                    this.usersStoreService.loadCustomers();
                    this.agentsStoreService.loadAgents();
                    this.collaborationSpaceStoreService.loadHasMultipleCollaborationSpaces();
                    this.notificationsStoreService.loadNotifications();
                }
            });

        combineLatest([
            this.settingsStoreService.getSettings(),
            this.configurationService.configuration$,
            this.userStoreService.getIsAuthorized()
        ]).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(([settings, configuration, isUserAuthorized]) => {
            if (settings != null && settings.layoutSettings != null && configuration.company != null) {
                this.isDarkMode = isUserAuthorized ? settings.layoutSettings.darkMode : false;
                this.setTheme(configuration.company.theme, configuration.company.darkMode);
            }
        });

        this.userStoreService.getCompanyId()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(companyId => {
                if (companyId != null) {
                    this.appStoreService.loadCompanyConfigurationByInternalId(companyId);
                } else {
                    this.appStoreService.loadDomainCompanyConfiguration();
                }
            });

        this.userStoreService.isPrimaryCustomer$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(isPrimary => {
                if (isPrimary) {
                    this.profileBaseStoreService.loadAdditionalProfiles({ isActiveOnly: false });
                }
            });
    }

    public ngAfterViewInit(): void {
        PageRefresherService.rewriteMobilePageRefresh('.global-content', '.toolbar-wrap', ['.map-view', '.map-view-wrap']);
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    @HostListener('window:beforeunload')
    public onWindowUnload(): void {
        this.userStoreService.getUser()
            .pipe(take(1))
            .subscribe(user => {
                if (user != null) {
                    this.communicationSignalRService.removeNotificationHub();
                    this.sleep(500);
                }
            });
    }

    public toggleSidebarOpen(key): void {
        this.sidebarService.getSidebar(key).toggleOpen();
    }

    private createAppConfigurationSubscription(): void {
        this.configurationService.configuration$
            .pipe(takeUntil(this.unsubscribe$))
            // eslint-disable-next-line complexity
            .subscribe((configuration: AppConfiguration) => {
                if (
                    configuration.company != null &&
                    (this.configuration == null || this.configuration.company != null)
                ) {
                    this.setTheme(configuration.company.theme, configuration.company.darkMode);
                }

                if (
                    this.dynamicComponentHost != null && this.dynamicComponentHost.viewContainerRef != null &&
                    configuration.layout != null &&
                    (this.configuration == null || this.configuration.layout != null &&
                        (this.configuration.layout.secondaryToolbar.hidden !== configuration.layout.secondaryToolbar.hidden ||
                            this.configuration.layout.secondaryToolbar.component !== configuration.layout.secondaryToolbar.component)
                    )
                ) {
                    this.dynamicComponentHost.viewContainerRef.clear();
                    if (!configuration.layout.secondaryToolbar.hidden && configuration.layout.secondaryToolbar.component != null) {
                        const componentFactory = this.componentFactoryResolver
                            .resolveComponentFactory(configuration.layout.secondaryToolbar.component);
                        this.dynamicComponentHost.viewContainerRef.createComponent<DynamicComponentHostDirective>(componentFactory);
                    }
                }

                this.configuration = configuration;
            });
    }

    private setTheme(themeName: string, themeDarkModeName: string): void {
        if (themeName == null) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < this.document.body.classList.length; i++) {
            const className = this.document.body.classList[i];

            if (className.startsWith('theme-')) {
                this.document.body.classList.remove(className);
            }
        }

        this.isDarkMode ? this.document.body.classList.add(themeDarkModeName) : this.document.body.classList.add(themeName);
    }

    private createCompanyConfigurationSubscription(): void {
        combineLatest([
            this.routeService.getCurrentRouteDataProperty<string>('title'),
            this.getCompanyNamePipe()
        ]).pipe(
            takeUntil(this.unsubscribe$),
            map(([providedTitle, companyName]) => this.getCorrectedTitle(providedTitle, companyName))
        ).subscribe(title => {
            if (title != null) {
                this.titleService.setTitle(title);
            }
        });
    }

    private getCompanyNamePipe(): Observable<string> {
        return this.configurationService.configuration$
            .pipe(
                takeUntil(this.unsubscribe$),
                map((configuration: AppConfiguration) => configuration.company.companyName)
            );
    }

    private getCorrectedTitle(providedTitle: string, companyName: string): string {
        return providedTitle != null && companyName != null
            ? providedTitle + ' | ' + companyName
            : null;
    }

    private loadSettings(): void {
        this.userStoreService.getIsAuthorized()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(isUserAuthorized => {
                if (isUserAuthorized) {
                    this.settingsStoreService.loadSettings();
                    this.settingsStoreService.loadNeighborhoodsMapping();
                }
            });
    }

    private subscribeToLayoutReset(): void {
        // Reload the default layout config on every RoutesRecognized event
        // if the current layout config is different from the default one
        // to rewert layout changes done on pages
        this.routeService.getCurrentRouteDataProperty<boolean>('applyDefaultLayout', RoutesRecognized)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(applyDefaultLayout => {
                this.configurationService.resetLayout((applyDefaultLayout == null || applyDefaultLayout));
            });
    }

    /**
     * Syncronious delay
     * 
     * @param delay Delay in milliseconds
     */
    private sleep(delay): void {
        const start = new Date().getTime();
        while (new Date().getTime() < start + delay);
    }
}